import { c as _c } from "react/compiler-runtime";
import React, { MouseEvent, ReactElement, ReactNode, useState } from 'react';
import ImageComponent from '@spa/components/image-component/image.component';
import Link from 'next/link';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import classNames from 'clsx';
import styles from './category-entries.module.scss';
import { tracker } from '@spa/lib/tracking/tracker';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useRuntimeSearchParams from '@spa/lib/react/hooks/useRuntimeSearchParams';
type IconPosition = 'left' | 'right';
interface ChildItemProps {
  readonly className?: string;
  readonly isExpandableList: boolean;
  readonly isCategoryPage: boolean;
  readonly isOpen: boolean;
  readonly isRedirected: boolean;
  readonly label: string;
  readonly path: string | null;
  readonly onExpand: () => void;
}
interface ParentItemProps {
  readonly iconPosition?: IconPosition;
  readonly isCategoryPage: boolean;
  readonly isRedirected: boolean;
  readonly items: ReadonlyArray<Pick<Entry, 'label' | 'path'>>;
  readonly label: string;
  readonly path: string | null;
}
interface NavigationItemContentProps {
  readonly children: ReactNode;
  readonly isCategoryPage: boolean;
  readonly isRedirected: boolean;
  readonly label: string;
  readonly path: string | null;
}
export interface Entry {
  readonly label: string;
  readonly path: string | null;
  readonly items: ReadonlyArray<Pick<Entry, 'label' | 'path'>>;
}
interface CategoryEntriesProps {
  readonly entries: ReadonlyArray<Entry>;
  readonly isCategoryPage: boolean;
}
const ArrowIcon = t0 => {
  const $ = _c(4);
  const {
    iconPosition: t1
  } = t0;
  const iconPosition = t1 === undefined ? "left" : t1;
  const t2 = styles[`category-entries__icon--${iconPosition}`];
  let t3;
  if ($[0] !== t2) {
    t3 = classNames(styles["category-entries__icon"], t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t3) {
    t4 = <ImageComponent src="/assets/icons/arrow_forward.svg" height={15} width={15} alt="" className={t3} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
};
const NavigationItemContent = t0 => {
  const $ = _c(8);
  const {
    children,
    isCategoryPage,
    isRedirected,
    label,
    path
  } = t0;
  let t1;
  if ($[0] !== isCategoryPage || $[1] !== label) {
    t1 = event => {
      if (isCategoryPage) {
        tracker.navigationClick({
          click_name: label,
          click_position: "side_navi",
          click_url: event.currentTarget.href
        });
      }
    };
    $[0] = isCategoryPage;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleClick = t1;
  let t2;
  if ($[3] !== children || $[4] !== handleClick || $[5] !== isRedirected || $[6] !== path) {
    t2 = path ? <Link prefetch={false} href={path} className={styles["category-entries__link"]} data-track-id={isRedirected ? "redirectSubSelector" : undefined} onClick={handleClick}>{children}</Link> : <span className={styles["category-entries__link"]}>{children}</span>;
    $[3] = children;
    $[4] = handleClick;
    $[5] = isRedirected;
    $[6] = path;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const ChildItem = t0 => {
  const $ = _c(21);
  const {
    label,
    path,
    onExpand,
    isOpen,
    isExpandableList,
    isCategoryPage,
    isRedirected
  } = t0;
  const {
    formatMessage
  } = useNamespacedIntl("sidebar");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ArrowIcon />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== label) {
    t2 = <>{t1}<span>{label}</span></>;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const labelAndIcon = t2;
  const t3 = isExpandableList && !isOpen;
  let t4;
  if ($[3] !== t3) {
    t4 = classNames(styles["category-entries__sub-list__list-item"], {
      [styles["category-entries__sub-list__list-item--closed"]]: t3
    });
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const visibleListItemClasses = t4;
  const t5 = path ?? label;
  let t6;
  if ($[5] !== isCategoryPage || $[6] !== isRedirected || $[7] !== label || $[8] !== labelAndIcon || $[9] !== path) {
    t6 = <NavigationItemContent isCategoryPage={isCategoryPage} isRedirected={isRedirected} label={label} path={path}>{labelAndIcon}</NavigationItemContent>;
    $[5] = isCategoryPage;
    $[6] = isRedirected;
    $[7] = label;
    $[8] = labelAndIcon;
    $[9] = path;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== formatMessage || $[12] !== isExpandableList || $[13] !== isOpen || $[14] !== onExpand) {
    t7 = isExpandableList && <button onClick={onExpand}>{formatMessage(isOpen ? "showLess" : "showMore")}</button>;
    $[11] = formatMessage;
    $[12] = isExpandableList;
    $[13] = isOpen;
    $[14] = onExpand;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t6 || $[18] !== t7 || $[19] !== visibleListItemClasses) {
    t8 = <li key={t5} className={visibleListItemClasses}>{t6}{t7}</li>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = visibleListItemClasses;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
const ParentItem = ({
  label,
  path,
  iconPosition,
  isCategoryPage,
  isRedirected,
  items
}: ParentItemProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const hasHiddenItems = items.length > 10;
  const visibleItems = isOpen ? items : items.slice(0, 10);
  const subListClasses = classNames(styles['category-entries__sub-list'], {
    [`${styles['category-entries__sub-list--with-children']}`]: items.length > 0
  });
  const labelAndIcon = <>
      {iconPosition === 'left' ? <ArrowIcon /> : null}
      <span>{label}</span>
      {iconPosition === 'right' ? <ArrowIcon iconPosition={iconPosition} /> : null}
    </>;
  return <li key={path ?? label} className={styles['category-entries__list-item--parent']}>
      <NavigationItemContent isCategoryPage={isCategoryPage} isRedirected={isRedirected} label={label} path={path}>
        {labelAndIcon}
      </NavigationItemContent>
      {visibleItems.length > 0 && <ul className={subListClasses}>
          {visibleItems.map(({
        label: itemLabel,
        path: itemPath
      }, index) => {
        const isLastVisibleItem = index === visibleItems.length - 1;
        return <ChildItem key={`${itemLabel}${itemPath}`} label={itemLabel} path={itemPath} onExpand={() => setIsOpen(!isOpen)} isOpen={isOpen} isExpandableList={hasHiddenItems && isLastVisibleItem} isCategoryPage={isCategoryPage} isRedirected={isRedirected} />;
      })}
        </ul>}
    </li>;
};
const CategoryEntries = t0 => {
  const $ = _c(8);
  const {
    entries,
    isCategoryPage
  } = t0;
  const searchParams = useRuntimeSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams?.get("redirected");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isRedirected = t1 === "true";
  let t2;
  if ($[2] !== entries || $[3] !== isCategoryPage || $[4] !== isRedirected) {
    t2 = entries.length > 0 ? <ul className={styles["category-entries"]}>{entries.map(t3 => {
        const {
          label,
          path,
          items
        } = t3;
        return <ParentItem label={label} path={path} iconPosition="right" isCategoryPage={isCategoryPage} isRedirected={isRedirected} items={items} key={`${label}${path}`} />;
      })}</ul> : null;
    $[2] = entries;
    $[3] = isCategoryPage;
    $[4] = isRedirected;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <>{t2}</>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
export default CategoryEntries;